import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import * as AppPropTypes from '../../lib/PropTypes';

import { useThemeClassName } from '../../contexts/ThemeContext';
import NhlLivePeriod from './SportLivePeriod';

import styles from '../../../styles/partials/nfl-live-game-stats-detailed.module.scss';

const stats = [
    {
        key: 'passingYardsCount',
        label: <FormattedMessage defaultMessage="Passing Yards" description="Stats label" />,
    },
    {
        key: 'rushingYardsCount',
        label: <FormattedMessage defaultMessage="Rushing Yards" description="Stats label" />,
    },
    {
        key: 'yardsByPlayAvg',
        label: (
            <FormattedMessage defaultMessage="Average Yards per Play" description="Stats label" />
        ),
    },
    {
        key: 'score',
        label: <FormattedMessage defaultMessage="Points Scored" description="Stats label" />,
    },
    {
        key: 'fumblesLostCount',
        label: <FormattedMessage defaultMessage="Fumbles Lost" description="Stats label" />,
    },
    {
        key: 'interceptionsThrownCount',
        label: <FormattedMessage defaultMessage="Interceptions" description="Stats label" />,
    },
    {
        key: 'sacksAllowedCount',
        label: <FormattedMessage defaultMessage="Sacks Allowed" description="Stats label" />,
    },
    {
        key: 'thirdDownPercentage',
        label: <FormattedMessage defaultMessage="3rd Down Efficiency" description="Stats label" />,
    },
    {
        key: 'timeOfPossession',
        label: <FormattedMessage defaultMessage="Time of Possession" description="Stats label" />,
    },
    {
        key: 'penalties',
        label: <FormattedMessage defaultMessage="Penalties" description="Stats label" />,
    },
];

const propTypes = {
    withLogo: PropTypes.bool,
    sportGame: AppPropTypes.sportGame,
    className: PropTypes.string,
};

const defaultProps = {
    withLogo: false,
    sportGame: null,
    className: null,
};

function NflLiveGameStatsDetailed({ withLogo, sportGame, className }) {
    const themeClassName = useThemeClassName(styles);
    const {
        periods = [],
        teamAway = null,
        teamHome = null,
        players: initialPlayers = null,
    } = sportGame || {};
    const [players] = useState(initialPlayers);

    const { length: playersCount } = players || [];
    const hasPlayers = playersCount > 0;

    const teams = useMemo(
        () =>
            teamAway !== null && teamHome !== null
                ? [
                      { ...teamAway, isHome: false },
                      { ...teamHome, isHome: true },
                  ]
                : [],
        [teamAway, teamHome],
    );

    const { score: teamAwayScore, abbreviation: teamAwayAbbreviation = null } = teamAway || {};
    const { score: teamHomeScore, abbreviation: teamHomeAbbreviation = null } = teamHome || {};

    const tableRows = useMemo(() => {
        const rows = stats.map(({ key, label }) => {
            if (teamAway[key] !== (undefined || null) && teamHome[key] !== (undefined || null)) {
                return (
                    <tr key={key}>
                        <td>{label}</td>
                        <td className={styles.abbreviation}>{teamAwayAbbreviation}</td>
                        <td>
                            {teamAway[key]}
                            {key === 'thirdDownPercentage' ? '%' : null}
                        </td>
                        <td className={styles.abbreviation}>{teamHomeAbbreviation}</td>
                        <td>
                            {teamHome[key]}
                            {key === 'thirdDownPercentage' ? '%' : null}
                        </td>
                    </tr>
                );
            }
            return null;
        });

        return rows;
    }, [teamHome, teamAway]);

    return (
        <div
            className={classNames([
                styles.container,
                {
                    [styles.hideDetails]: !hasPlayers,
                },
                themeClassName,
                className,
            ])}
        >
            <div className={styles.topBanner}>
                <div className={styles.inner}>
                    <div className={styles.main}>
                        <div className={styles.teamsMain}>
                            {teams.map(({ name, location, logo, standings = null }, teamIndex) => {
                                const { url: logoUrl = null } = logo || {};
                                const { wins = 0, losses = 0, ot = 0 } = standings || {};
                                return (
                                    <div key={`team-${teamIndex + 1}`} className={styles.teamMain}>
                                        {logoUrl !== null && withLogo ? (
                                            <img className={styles.logo} src={logoUrl} alt={name} />
                                        ) : null}
                                        <div className={styles.name}>{`${location}`}</div>
                                        {standings !== null ? (
                                            <div
                                                className={styles.wlo}
                                            >{`(${wins}-${losses}-${ot})`}</div>
                                        ) : null}
                                    </div>
                                );
                            })}
                        </div>
                        <div className={styles.liveScore}>
                            <span className={styles.liveGoals}>{teamAwayScore}</span>
                            <span className={styles.separator}>
                                <sup>-</sup>
                            </span>
                            <span className={styles.liveGoals}>{teamHomeScore}</span>
                        </div>
                    </div>
                    <div className={styles.livePeriodContainer}>
                        <NhlLivePeriod className={styles.livePeriod} sportGame={sportGame} />
                    </div>
                </div>
            </div>
            <div className={styles.spacer} />
            <div className={styles.inner}>
                <table className={styles.teamGoalsPeriods}>
                    <thead>
                        <tr>
                            <td>
                                <FormattedMessage defaultMessage="Teams" description="Label" />
                            </td>
                            {(periods || []).map(({ number }, periodIndex) => (
                                <td key={`period-${periodIndex + 1}`}>{number}</td>
                            ))}
                            <td>
                                <FormattedMessage
                                    defaultMessage="T"
                                    description="NHL stats - Total"
                                />
                            </td>
                        </tr>
                    </thead>
                    <tbody>
                        {teams.map(({ name, location, isHome }, teamIndex) => (
                            <tr key={`team-${teamIndex + 1}`} className={styles.teamName}>
                                <td>{withLogo ? name : location}</td>
                                {(periods || []).map(
                                    ({ teamHome: th, teamAway: ta }, periodIndex) => {
                                        const { score } = (isHome ? th : ta) || {};
                                        return <td key={`period-${periodIndex + 1}`}>{score}</td>;
                                    },
                                )}
                                <td>{isHome ? teamHomeScore : teamAwayScore}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                <h3 className={styles.tableTitle}>
                    <FormattedMessage
                        defaultMessage="Team stats box score"
                        description="Nfl stats table title"
                    />
                </h3>

                <table className={styles.statsTable}>
                    <tbody>{tableRows}</tbody>
                </table>
            </div>
        </div>
    );
}

NflLiveGameStatsDetailed.propTypes = propTypes;
NflLiveGameStatsDetailed.defaultProps = defaultProps;

export default NflLiveGameStatsDetailed;
